import {
  type ApplicationStatusLabelID,
  type DealsDashboardTab,
  type DealsSearchParams,
  type DealTypeScopeFilter,
  type ExportDealsParams,
  type FilterDataType,
  type GetDealDocumentHTTPHandlerResponse,
  type GetDetailedDealDocumentHTTPHandlerResponse,
  type GetLandlordBuildingsHTTPHandlerResponse,
  type GetLandlordDealsExportHTTPHandlerResponse,
  type GetLandlordDealsHTTPHandlerResponseData,
  type GetLandlordDealsPaginationResponseData,
  type LeaseDateFilters,
} from './application.types';
import { ApplicationTabId } from './application-query.constants';

import type { DateRangeFilter } from '@/shared/components/DatePicker';

import { apiUri } from '@/constants/Constants';
import { DateFormat } from '@/shared/libs/time.lib';
import { DealsDashboardClient } from '@/shared/api';

const TAB_SCOPE_FILTERS: Record<DealsDashboardTab, DealTypeScopeFilter> = {
  [ApplicationTabId.AllApplications]: {},
  [ApplicationTabId.NewApplications]: { is_renewal: false },
  [ApplicationTabId.ExpiringSoon]: { is_expiring_soon: true },
  [ApplicationTabId.RenewalApplications]: { is_renewal: true },
  [ApplicationTabId.AllRecords]: {},
  [ApplicationTabId.LeaseDetailsNeeded]: {
    is_renewal: false,
    deal_status: { in: ['provide-lease-details'] },
  },
  [ApplicationTabId.RenewalDecisionNeeded]: { is_expiring_soon: true },
  [ApplicationTabId.IssuedBonds]: { deal_status: { in: ['deal-closed'] } },
};

export const getTabScopeFilter = (
  applicationTab: DealsDashboardTab,
  includeExpiredDeals = false,
): DealTypeScopeFilter => {
  const scope = TAB_SCOPE_FILTERS[applicationTab];

  // Only sent when on: the backend's default already hides the long-expired bonds, and
  // omitting it keeps the payload the same as before the toggle existed.
  return scope.is_expiring_soon && includeExpiredDeals
    ? { ...scope, include_expired_deals: true }
    : scope;
};

/**
 * Builds the `deal_status` portion of the search payload. Every status — bond issued
 * included — is a status-pill label id, so `deal_status` is the only status contract.
 */
const buildDealStatusFilters = (
  selectedStatus: FilterDataType[] | undefined,
): { deal_status?: { in: string[] } } => {
  if (!selectedStatus?.length) {
    return {};
  }
  const labelIds = Array.from(
    new Set(
      selectedStatus
        .map((item) => item.labelId)
        .filter((id): id is ApplicationStatusLabelID => Boolean(id)),
    ),
  );
  return labelIds.length ? { deal_status: { in: labelIds } } : {};
};

const buildSearchTextFilter = (search: string | undefined): { search_text?: string } =>
  search ? { search_text: search.trim().replaceAll(/\s\s+/g, ' ') } : {};

const buildRenewalOpportunityStatusFilter = (
  renewalOpportunityStatus: string[] | undefined,
): { renewal_opportunity_status?: { in: string[] } } =>
  renewalOpportunityStatus?.length
    ? { renewal_opportunity_status: { in: renewalOpportunityStatus } }
    : {};

const buildBuildingFilter = (
  selectedBuildings: FilterDataType[] | undefined,
): { building_id?: { in: string[] } } =>
  selectedBuildings?.length
    ? { building_id: { in: selectedBuildings.map((item) => item.id) } }
    : {};

const formatDateRange = ({ from, to }: DateRangeFilter) => ({
  from: from?.format(DateFormat.Calendar),
  to: to?.format(DateFormat.Calendar),
});

const buildLeaseFilter = (leaseFilter: LeaseDateFilters | undefined) =>
  leaseFilter
    ? {
        lease: {
          lease_start_date: formatDateRange(leaseFilter.startDateRange),
          lease_end_date: formatDateRange(leaseFilter.endDateRange),
        },
      }
    : {};

const createSearchQuery = ({
  applicationTab,
  limit,
  pageCursor,
  search,
  selectedStatus,
  selectedBuildings,
  leaseDates,
  sortField,
  sortDirection,
  renewalOpportunityStatus,
  includeExpiredDeals = false,
}: DealsSearchParams) => ({
  filters: {
    ...buildSearchTextFilter(search),
    ...buildDealStatusFilters(selectedStatus),
    ...buildRenewalOpportunityStatusFilter(renewalOpportunityStatus),
    ...buildBuildingFilter(selectedBuildings),
    ...getTabScopeFilter(applicationTab, includeExpiredDeals),
    ...buildLeaseFilter(leaseDates),
  },
  pagination: {
    ...(limit && { limit }),
    ...(pageCursor && { page_cursor: pageCursor }),
  },
  ...(sortField && sortDirection && { sort: { field: sortField, direction: sortDirection } }),
});

type LandlordDealsResponseData<IsFromPagination extends boolean | undefined> =
  IsFromPagination extends true
    ? GetLandlordDealsPaginationResponseData
    : GetLandlordDealsHTTPHandlerResponseData;

type GetLandlordDealsParams<IsFromPagination extends boolean | undefined> = DealsSearchParams & {
  limit: number;
  isFromPagination?: IsFromPagination;
};

export const getLandlordDeals = async <IsFromPagination extends boolean | undefined = undefined>({
  isFromPagination,
  ...searchParams
}: GetLandlordDealsParams<IsFromPagination>): Promise<
  LandlordDealsResponseData<IsFromPagination>
> => {
  const response = await DealsDashboardClient.post<{
    data: LandlordDealsResponseData<IsFromPagination>;
  }>(
    isFromPagination ? apiUri.getAllDealsRecordsCount : apiUri.getAllDeals,
    createSearchQuery(searchParams),
  );

  return response.data.data;
};

export const exportDeals = async ({
  allRecords,
  ...searchParams
}: ExportDealsParams): Promise<GetLandlordDealsExportHTTPHandlerResponse> => {
  const payloadObject = allRecords
    ? {
        all_records: allRecords,
        queries: {
          filters: {
            ...getTabScopeFilter(searchParams.applicationTab, searchParams.includeExpiredDeals),
            ...buildRenewalOpportunityStatusFilter(searchParams.renewalOpportunityStatus),
          },
        },
      }
    : { all_records: allRecords, queries: createSearchQuery(searchParams) };

  const response = await DealsDashboardClient.post<GetLandlordDealsExportHTTPHandlerResponse>(
    apiUri.getAllDealsExport,
    payloadObject,
  );
  return response.data;
};

export const getLandlordBuildings = async (filters: {
  searchText?: string;
  page?: number;
  limit?: number;
}) => {
  const queryParams: Record<string, string | number> = {};
  if (filters.searchText) {
    queryParams.search_text = filters.searchText;
  }
  if (filters.page) {
    queryParams.page = filters.page;
  }
  if (filters.limit) {
    queryParams.limit = filters.limit;
  }
  const response = await DealsDashboardClient.get<GetLandlordBuildingsHTTPHandlerResponse>(
    apiUri.getLandlordBuildings,
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDealBondDocument = async (dealId: string, source: string) => {
  const queryParams: Record<string, string | number> = {
    document_type: 'signed-bond',
    source,
  };
  const response = await DealsDashboardClient.get<GetDealDocumentHTTPHandlerResponse>(
    apiUri.getDealBondDocument.replace('{dealId}', dealId),
    {
      params: queryParams,
    },
  );

  return response.data.data;
};

export const getDetailedDocument = async (dealId: string, source: string) => {
  const response = await DealsDashboardClient.get<GetDetailedDealDocumentHTTPHandlerResponse>(
    apiUri.getDetailedDealDocument.replace('{dealId}', dealId),
    {
      params: {
        source,
      },
    },
  );

  return response.data.data;
};
